"use client";

import { useQuery } from "@tanstack/react-query";
import { getBlokComponent } from "../../defineBloks";
import type {
  BaseSBType,
  BlokComponent,
  BlokWithFallback,
  BlokWithFetcher,
} from "../../type-generators";
import type { StoryblokComponentProps } from "./types";

export const ClientComponent = ({
  blok,
  preview,
  ...rest
}: StoryblokComponentProps) => {
  const Component = getBlokComponent(blok.component);
  if (!Component) {
    const errorMessage = `Component: "${blok.component}" not found`;
    return <div>{errorMessage}</div>;
  }

  if (typeof Component === "object") {
    if ("fetcher" in Component) {
      return (
        <ClientComponentWithFetcher
          componentData={Component}
          preview={preview}
          {...rest}
          blok={blok}
        />
      );
    }
  }

  return (
    <ClientComponentOnly
      preview={preview}
      {...rest}
      blok={blok}
      componentData={Component}
    />
  );
};

const ClientComponentOnly = ({
  blok,
  componentData,
  preview,
  ...rest
}: StoryblokComponentProps & {
  componentData:
    | BlokWithFallback<BaseSBType, {}>
    | BlokComponent<BaseSBType, {}>;
}) => {
  const Blok =
    typeof componentData === "function" ? componentData : componentData.blok;

  return <Blok preview={preview} {...rest} blok={blok} />;
};

export const ClientComponentWithFetcher = ({
  blok,
  preview,
  componentData,
  ...rest
}: StoryblokComponentProps & {
  componentData: BlokWithFetcher<BaseSBType, {}>;
}) => {
  const keyRes = componentData.getKey?.({ blok, preview });
  const key = Array.isArray(keyRes) ? keyRes : [keyRes];

  const { data, isLoading } = useQuery({
    queryKey: ["blok", blok._uid, ...key.filter(Boolean)],
    queryFn: () => componentData.fetcher({ blok, preview }),
  });

  const Blok = componentData.blok;

  const Fallback = componentData.fallback;

  if (isLoading) {
    return Fallback ? <Fallback blok={blok} preview={preview} /> : null;
  }

  return <Blok {...rest} preview={preview} blok={blok} data={data as any} />;
};
